import React, { useEffect, useState } from 'react';
import { useTaskStatuses } from '../../hooks/useTaskStatuses';
import { TaskStatusDef, TaskStatusCategory, DEFAULT_TASK_STATUSES } from '../../types/task';
import { PlusIcon, TrashIcon, Bars3Icon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const CATEGORY_LABEL: Record<TaskStatusCategory, string> = {
  todo: 'Teendő (nyitott)',
  in_progress: 'Folyamatban (aktív)',
  completed: 'Kész (lezárt)',
  cancelled: 'Törölve / megszakítva'
};
const PALETTE = ['#9ca3af', '#0ea5e9', '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#f59e0b', '#10b981', '#14b8a6', '#f43f5e', '#0f7f75', '#64748b'];
const newId = () => `st_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const TaskStatusSettings: React.FC = () => {
  const { statuses, isCustom, loading, save } = useTaskStatuses();
  const [rows, setRows] = useState<TaskStatusDef[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);

  useEffect(() => { if (!dirty) setRows(statuses.map(s => ({ ...s }))); /* eslint-disable-next-line */ }, [statuses]);

  const update = (id: string, patch: Partial<TaskStatusDef>) => { setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r))); setDirty(true); };
  const remove = (id: string) => { setRows(rs => rs.filter(r => r.id !== id)); setDirty(true); };
  const add = () => { setRows(rs => [...rs, { id: newId(), name: 'Új státusz', color: PALETTE[rs.length % PALETTE.length], category: 'in_progress', order: rs.length }]); setDirty(true); };
  const move = (from: string, to: string) => {
    if (from === to) return;
    setRows(rs => {
      const a = rs.findIndex(r => r.id === from), b = rs.findIndex(r => r.id === to);
      if (a < 0 || b < 0) return rs;
      const next = [...rs]; const [it] = next.splice(a, 1); next.splice(b, 0, it); return next;
    });
    setDirty(true);
  };

  const validate = () => {
    if (rows.some(r => !r.name.trim())) return 'Minden státusznak kell név.';
    const cats = new Set(rows.map(r => r.category));
    if (!cats.has('todo')) return 'Legalább egy „Teendő” kategóriájú státusz kell (ide kerülnek az új feladatok).';
    if (!cats.has('completed')) return 'Legalább egy „Kész” kategóriájú státusz kell.';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    try { setSaving(true); await save(rows); setDirty(false); toast.success('Feladat-státuszok mentve'); }
    catch (e: any) { toast.error(e?.message || 'Mentés sikertelen'); }
    finally { setSaving(false); }
  };
  const reset = () => { setRows(DEFAULT_TASK_STATUSES.map(s => ({ ...s }))); setDirty(true); };

  if (loading) return <div className="text-sm text-gray-500">Betöltés…</div>;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Feladat-státuszok</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          A státuszok adják a Tábla oszlopait, az űrlap és a részletnézet státusz-választóját. Minden státusz egy <em>kategóriába</em> tartozik – ez dönti el, mi számít nyitottnak, késznek vagy lejártnak. {isCustom ? 'Egyedi lista aktív.' : 'Jelenleg az alapértelmezett lista érvényes.'}
        </p>
      </div>

      <div className="crm-card overflow-hidden">
        <div className="hidden sm:grid grid-cols-[28px_1fr_140px_220px_40px] gap-2 px-3 py-2 border-b border-[var(--crm-border)] text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <span /><span>Név</span><span>Szín</span><span>Kategória</span><span />
        </div>
        <ul className="divide-y divide-[var(--crm-border)]">
          {rows.map(r => (
            <li
              key={r.id}
              draggable
              onDragStart={() => setDragId(r.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) move(dragId, r.id); setDragId(null); }}
              className={`grid grid-cols-1 sm:grid-cols-[28px_1fr_140px_220px_40px] gap-2 items-center px-3 py-2 ${dragId === r.id ? 'opacity-50' : ''}`}
            >
              <span className="hidden sm:inline-flex cursor-grab text-gray-400"><Bars3Icon className="w-4 h-4" /></span>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
                <input value={r.name} onChange={e => update(r.id, { name: e.target.value })} className="w-full h-9 border rounded-lg px-3 text-sm" />
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {PALETTE.map(c => (
                  <button key={c} type="button" onClick={() => update(r.id, { color: c })} className={`w-4 h-4 rounded-full ring-offset-1 ring-offset-[var(--crm-surface)] ${r.color === c ? 'ring-2 ring-gray-900 dark:ring-white' : ''}`} style={{ backgroundColor: c }} aria-label={c} />
                ))}
              </div>
              <select value={r.category} onChange={e => update(r.id, { category: e.target.value as TaskStatusCategory })} className="h-9 pl-3 pr-8 text-sm border rounded-lg">
                {(Object.keys(CATEGORY_LABEL) as TaskStatusCategory[]).map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
              </select>
              <button type="button" onClick={() => remove(r.id)} disabled={rows.length <= 2} className="p-1.5 rounded-md text-gray-400 hover:text-rose-600 disabled:opacity-30 justify-self-end" aria-label="Törlés"><TrashIcon className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
        <div className="px-3 py-2 border-t border-[var(--crm-border)] flex items-center justify-between">
          <button type="button" onClick={add} className="crm-btn crm-btn-secondary text-xs h-8"><PlusIcon className="w-4 h-4 mr-1" />Új státusz</button>
          <span className="text-[11px] text-gray-400">Sorrend: húzd a sorokat.</span>
        </div>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Státusz törlésekor a benne lévő feladatok a kategóriájuk első státuszába kerülnek a listákon (az adat nem vész el).
      </p>

      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={reset} className="crm-btn crm-btn-ghost mr-auto text-xs"><ArrowPathIcon className="w-4 h-4 mr-1" />Alapértelmezett</button>
        <button type="button" onClick={handleSave} disabled={!dirty || saving} className="crm-btn crm-btn-primary">{saving ? 'Mentés…' : 'Mentés'}</button>
      </div>
    </div>
  );
};

export default TaskStatusSettings;
